import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, BadgeDollarSign, Award, Save } from "lucide-react";
import { API } from "../lib/site";
import { PLAN_INFO } from "../lib/plans";

const HotelsAdmin = ({ token }) => {
    const [hotels, setHotels] = useState([]);
    const [prices, setPrices] = useState({ essential: "", professional: "", leader: "" });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [h, p] = await Promise.all([
                axios.get(`${API}/admin/hotels`, authHeaders),
                axios.get(`${API}/admin/plans`, authHeaders),
            ]);
            setHotels(h.data.hotels || []);
            const pr = p.data.prices || {};
            setPrices({
                essential: pr.essential ? (pr.essential / 100).toString() : "",
                professional: pr.professional ? (pr.professional / 100).toString() : "",
                leader: pr.leader ? (pr.leader / 100).toString() : "",
            });
        } catch {
            toast.error("Could not load hotels and plans.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        load();
    }, [load]);

    const savePlans = async () => {
        setSaving(true);
        try {
            const toCents = (v) => {
                const n = parseFloat(v);
                return !v || isNaN(n) || n <= 0 ? null : Math.round(n * 100);
            };
            await axios.patch(
                `${API}/admin/plans`,
                {
                    essential: toCents(prices.essential),
                    professional: toCents(prices.professional),
                    leader: toCents(prices.leader),
                },
                authHeaders
            );
            toast.success("Membership prices updated.");
        } catch {
            toast.error("Could not save prices.");
        } finally {
            setSaving(false);
        }
    };

    const toggleRecognized = async (hotel) => {
        try {
            await axios.patch(`${API}/admin/hotels/${hotel.id}`, { recognized: !hotel.recognized }, authHeaders);
            setHotels((hs) => hs.map((x) => (x.id === hotel.id ? { ...x, recognized: !hotel.recognized } : x)));
            toast.success(hotel.recognized ? "Recognition removed." : "GGH Recognition awarded.");
        } catch {
            toast.error("Could not update the hotel.");
        }
    };

    return (
        <div className="mt-16 border-t border-white/10 pt-12" data-testid="hotels-admin">
            <p className="eyebrow-gold">Membership</p>
            <h2 className="font-display mt-2 text-2xl font-medium text-[#FDFBF7] sm:text-3xl">
                Plans & Member Hotels
            </h2>

            {/* Pricing editor */}
            <div className="mt-8 rounded-2xl border border-white/10 bg-[#07211A] p-7" data-testid="plans-editor">
                <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">
                    <BadgeDollarSign size={14} aria-hidden="true" /> Annual Membership Prices (USD)
                </p>
                <p className="mt-2 text-xs text-[#FDFBF7]/50">
                    Leave a plan empty to show "Price on request" on the website.
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-3">
                    {PLAN_INFO.map((p) => (
                        <div key={p.key}>
                            <label htmlFor={`price-${p.key}`} className="mb-2 block text-xs font-bold text-[#FDFBF7]/70">
                                {p.name}
                            </label>
                            <input
                                id={`price-${p.key}`}
                                data-testid={`plan-price-${p.key}`}
                                type="number"
                                min="0"
                                step="1"
                                value={prices[p.key]}
                                onChange={(e) => setPrices((pr) => ({ ...pr, [p.key]: e.target.value }))}
                                className="input-field !bg-[#04150D] !text-[#FDFBF7] !border-white/15"
                                placeholder="e.g. 490"
                            />
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    data-testid="plans-save"
                    onClick={savePlans}
                    disabled={saving}
                    className="btn-gold mt-5 !px-5 !py-2.5 disabled:opacity-60"
                >
                    <Save size={15} aria-hidden="true" /> {saving ? "Saving…" : "Save Prices"}
                </button>
            </div>

            {/* Hotels list */}
            <div className="mt-8 grid gap-4" data-testid="hotels-list">
                {loading ? (
                    <p className="py-10 text-center text-sm text-[#FDFBF7]/50">Loading hotels…</p>
                ) : hotels.length === 0 ? (
                    <p data-testid="hotels-admin-empty" className="rounded-2xl border border-white/10 bg-[#07211A]/60 py-12 text-center text-sm text-[#FDFBF7]/50">
                        No hotel accounts yet. New registrations will appear here.
                    </p>
                ) : (
                    hotels.map((h) => (
                        <article
                            key={h.id}
                            data-testid={`admin-hotel-${h.id}`}
                            className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-[#07211A] p-6"
                        >
                            <div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A]">
                                        <Building2 size={15} aria-hidden="true" />
                                    </span>
                                    <h3 className="font-display text-lg font-medium text-[#FDFBF7]">{h.hotel_name}</h3>
                                    <span className="rounded-full border border-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#FDFBF7]/60">
                                        {h.plan}
                                    </span>
                                    <span
                                        className={`rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider ${
                                            (h.membership || {}).status === "active"
                                                ? "bg-[#1A3B2B] text-[#D9B96A]"
                                                : "border border-[#C2A160]/50 text-[#D9B96A]"
                                        }`}
                                    >
                                        {(h.membership || {}).status || "none"}
                                    </span>
                                    {h.recognized && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-[#C2A160] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#0A1711]">
                                            <Award size={10} aria-hidden="true" /> Recognized
                                        </span>
                                    )}
                                </div>
                                <p className="mt-1.5 text-xs text-[#FDFBF7]/50">
                                    {h.email} · {h.location} · {h.rooms} rooms · Score:{" "}
                                    {(h.assessment || {}).overall != null ? `${h.assessment.overall}%` : "not assessed"}
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid={`hotel-recognize-${h.id}`}
                                onClick={() => toggleRecognized(h)}
                                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-[#FDFBF7]/80 transition-colors hover:border-[#C2A160] hover:text-[#C2A160]"
                            >
                                <Award size={14} aria-hidden="true" />
                                {h.recognized ? "Remove Recognition" : "Award Recognition"}
                            </button>
                        </article>
                    ))
                )}
            </div>
        </div>
    );
};

export default HotelsAdmin;
